import Layout from "@/components/feature/Layout";
import PageHero from "@/components/base/PageHero";
import SectionHeader from "@/components/base/SectionHeader";
import NAICSCodesSection from "@/components/base/NAICSCodesSection";

const companyProfile = [
  {
    label: "Legal Name",
    value: "Mitaja Corporation",
  },
  {
    label: "Business Status",
    value: "Small, for-profit corporation",
  },
  {
    label: "UEI",
    value: "C9PPAQCM5DD9",
  },
  {
    label: "CAGE Code",
    value: "7PBY4",
  },
  {
    label: "SBA 8(a)",
    value: "Certified participant",
  },
  {
    label: "Address",
    value: "8115 Maple Lawn Blvd., Suite 350, Fulton, MD 20759-2683",
  },
  {
    label: "Website",
    value: "mitajacorp.com",
  },
];

const contractHistory = [
  {
    label: "GSA MAS Contract",
    value: "47QTCA22D0083",
    note: "General Services Administration Multiple Award Schedule.",
  },
  {
    label: "Previous GSA IT Schedule 70 Contract",
    value: "47QTCA18D00DQ",
    note: "Expired June 17, 2023.",
  },
];

const contractingCapabilities = [
  {
    title: "IT Professional Services",
    description: "Qualified technical professionals and consulting resources for enterprise IT programs.",
    icon: "ri-service-line",
  },
  {
    title: "Custom Computer Programming",
    description: "Design and development of custom software and programming solutions.",
    icon: "ri-code-s-slash-line",
  },
  {
    title: "Systems Programming",
    description: "Low-level system software and platform-level programming services.",
    icon: "ri-cpu-line",
  },
  {
    title: "Database Administration",
    description: "Administration, maintenance, and optimization of enterprise database platforms.",
    icon: "ri-database-2-line",
  },
  {
    title: "Business-Intelligence Programming",
    description: "Data-driven programming and reporting solutions to support business intelligence.",
    icon: "ri-bar-chart-box-line",
  },
  {
    title: "IT Staffing & Workforce Solutions",
    description: "Flexible staffing and workforce solutions to meet program resource needs.",
    icon: "ri-team-line",
  },
  {
    title: "Network-Infrastructure Staffing",
    description: "Specialized technical staffing for network and infrastructure engineering.",
    icon: "ri-router-line",
  },
  {
    title: "Technology Talent Management",
    description: "Talent acquisition, management, and deployment for technology programs.",
    icon: "ri-user-star-line",
  },
];

export default function ContractingInformation() {
  return (
    <Layout>
      <PageHero
        title="Contracting Information"
        subtitle="Procurement Details"
        description="Contracting credentials and procurement information for government procurement officers and prime contractor partners."
        size="md"
      />

      <section className="section-padding bg-white">
        <div className="container-page max-w-5xl mx-auto">
          <SectionHeader
            title="Company Profile"
            description="Verified business and registration details for federal procurement and contracting."
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {companyProfile.map((item) => (
              <div key={item.label} className="card-base p-6">
                <h3 className="text-xs font-bold text-accent-700 uppercase tracking-wider mb-2">
                  {item.label}
                </h3>
                <p className="text-base font-semibold text-foreground-900 leading-snug">
                  {item.value}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-16">
            <NAICSCodesSection />
          </div>
        </div>
      </section>

      <section className="section-padding bg-background-100">
        <div className="container-page max-w-5xl mx-auto">
          <SectionHeader
            title="Contract Vehicles"
            description="Active and historical GSA contract information."
          />

          <div className="space-y-6">
            {contractHistory.map((item) => (
              <div key={item.label} className="card-base p-6">
                <div className="flex flex-col md:flex-row md:items-start gap-4">
                  <div className="md:w-64 shrink-0">
                    <h3 className="text-sm font-bold text-foreground-900 uppercase tracking-wider">
                      {item.label}
                    </h3>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-accent-600 mb-1">
                      {item.value}
                    </p>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {item.note}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-background-100">
        <div className="container-page max-w-5xl mx-auto">
          <SectionHeader
            title="State Certifications"
            description="Certified small and diverse business designations for state and local procurement."
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                abbr: "MBE",
                name: "Minority Business Enterprise",
                description: "Maryland-certified Minority Business Enterprise (MBE) — Certification No. 17-059 for state and local government contracting.",
              },
              {
                abbr: "DBE",
                name: "Disadvantaged Business Enterprise",
                description: "Maryland-certified Disadvantaged Business Enterprise (DBE) — Certification No. 17-059 supporting DOT-assisted and transportation-related procurements.",
              },
              {
                abbr: "SBE",
                name: "Small Business Enterprise",
                description: "Maryland-certified Small Business Enterprise (SBE) — Certification No. 17-059 for small-business set-aside and preference programs.",
              },
            ].map((cert) => (
              <div key={cert.abbr} className="card-base p-6">
                <div className="w-12 h-12 rounded-md bg-accent-100 flex items-center justify-center mb-3">
                  <span className="text-sm font-bold text-accent-700">{cert.abbr}</span>
                </div>
                <h3 className="text-sm font-bold text-foreground-900 mb-1">{cert.name}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{cert.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-page max-w-5xl mx-auto">
          <SectionHeader
            title="Contracting Capabilities"
            description="The technical and professional services Mitaja delivers through its contracting vehicles."
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {contractingCapabilities.map((cap) => (
              <div key={cap.title} className="card-base p-6 flex items-start gap-4">
                <div className="w-11 h-11 shrink-0 rounded-md bg-accent-100 flex items-center justify-center">
                  <i className={`${cap.icon} text-accent-700 text-lg`}></i>
                </div>
                <div>
                  <h3 className="text-base font-bold text-foreground-900 mb-1">
                    {cap.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {cap.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}